import React, {
    useCallback,
    useEffect, useLayoutEffect,
    useRef,
    useState,
} from "react";
import { Checkbox } from "antd";
import ButtonsGroup from "./ButtonsGroup";
import Button from "./Button";
import { FilterFilled, FilterOutlined } from "@ant-design/icons";
import {useTableFocus} from "@context/TableFocusContext";

const createTableAntDHeaderFilter = (title, key, dataIndex, filtersArr, onFilterVal) => {
    const [filters, setFilters] = useState([]);
    const [isDropDownVisible, setDropDownVisible] = useState(false);
    const lastEventWasKeyboard = useRef(false);
    const dropDownRef = useRef(null);
    const checkboxRefs = useRef([]),
        checkboxIndex = useRef(0),
        firstButtonRef = useRef();

    useLayoutEffect(() => {
        if (isDropDownVisible && lastEventWasKeyboard.current) {
            setTimeout(()=>{
                requestAnimationFrame(() => {
                    const targetCheckbox = checkboxRefs.current[checkboxIndex.current];
                    if (targetCheckbox) {
                        targetCheckbox.focus();
                    }
                });
            },0);
        }
    }, [isDropDownVisible]);

    const toggleDropDown = useCallback((value) => {
        setDropDownVisible(value);
        lastEventWasKeyboard.current = !!lastEventWasKeyboard;
    }, []);

    const FilterDropdownContent = useCallback(({ columnIndex, tableId }) => {
        const [tempFilters, setTempFilters] = useState(filters);
        const {
            setSelectedIndex,
        } = useTableFocus();

        const closeAndFocus = (e) => {
            setDropDownVisible(false);

            if (columnIndex !== undefined && columnIndex !== null) {
                setSelectedIndex(columnIndex);
            }
        };

        const handleApply = (e) => {
            setFilters(tempFilters);
            closeAndFocus(e);
            lastEventWasKeyboard.current = false;
        };

        const handleClose = (e) => {
            closeAndFocus(e);
            lastEventWasKeyboard.current = false;
        };

        const handleReset = (e) => {
            setTempFilters([]);
            setFilters([]);
            closeAndFocus(e);
            lastEventWasKeyboard.current = false;
        };

        const handleFilterChange = (value) => {
            setTempFilters((prev) =>
                prev.includes(value)
                    ? prev.filter((v) => v !== value)
                    : [...prev, value]
            );
        };

        const onCheckboxKeyDown = (e) => {
            const total = checkboxRefs.current.length;
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                checkboxIndex.current = (checkboxIndex.current + 1) % total;
                checkboxRefs.current[checkboxIndex.current]?.focus();
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                checkboxIndex.current =
                    (checkboxIndex.current - 1 + total) % total;
                checkboxRefs.current[checkboxIndex.current]?.focus();
            } else if(e.key === 'Tab' && e.shiftKey){
                if(checkboxIndex.current !== 0){
                    e.preventDefault();
                    checkboxIndex.current =
                        (checkboxIndex.current - 1 + total) % total;
                    checkboxRefs.current[checkboxIndex.current]?.focus();
                }
            } else if(e.key === 'Tab'){
                e.preventDefault();
                if((checkboxIndex.current + 1) !== total){
                    checkboxIndex.current = checkboxIndex.current + 1;
                    checkboxRefs.current[checkboxIndex.current]?.focus();
                }else{
                    firstButtonRef.current.focus();
                }
            }
        };

        useEffect(() => {
            const node = dropDownRef.current;
            if (!node) return;

            node.addEventListener('keydown', dropDownKeyDown);
            document.addEventListener('click', handleClickOutside);

            return () => {
                node.removeEventListener('keydown', dropDownKeyDown);
                document.removeEventListener('click', handleClickOutside);
            };
        },[]);

        const handleClickOutside = (e) => {
            if (!dropDownRef.current?.contains(e.target)) {
                const node = dropDownRef.current;
                if (!node) return;
                node.removeEventListener('keydown', dropDownKeyDown);
                document.removeEventListener('click', handleClickOutside);
                closeAndFocus(e);
            }
        }

        const dropDownKeyDown = (e)=>{
            if(e.code === 'Escape'){
                closeAndFocus(e);
            }
        }

        return (
            <div id={tableId+''+columnIndex} className="ch-drop-down-wrapper" ref={dropDownRef}>
                <ul className="ant-dropdown-menu">
                    {filtersArr.map((filter,mapIdx) => (
                        <li
                            key={filter.text}
                            className={
                                "ant-dropdown-menu-item" +
                                (tempFilters.includes(filter.value)
                                    ? " ant-dropdown-menu-item-selected"
                                    : "")
                            }
                            onClick={() => handleFilterChange(filter.value)}
                        >
                            <Checkbox
                                ref={(el) => {
                                    if (el) {
                                        checkboxRefs.current[mapIdx] = el.input;
                                    }
                                }}
                                tabIndex={0}
                                onKeyDown={onCheckboxKeyDown}
                                aria-label={filter.text}
                                value={filter.value}
                                checked={tempFilters.includes(filter.value)}
                            >
                                <span className="padding-l--m">{filter.text}</span>
                            </Checkbox>
                        </li>
                    ))}
                </ul>
                <ButtonsGroup className="padding--m border-t--s">
                    <Button ref={firstButtonRef} tabIndex={0} type={'text'} onClick={handleClose} onKeyDown={(e)=>{if(e.code === 'Enter'){handleClose(e)}}}>
                        Close
                    </Button>
                    <Button tabIndex={0} type="text" onClick={handleReset} onKeyDown={(e)=>{if(e.code === 'Enter'){handleReset(e)}}}>
                        Reset
                    </Button>
                    <Button tabIndex={0} onClick={handleApply} onKeyDown={(e)=>{if(e.code === 'Enter'){handleApply(e)}else if(e.code === 'Tab'){
                        if(!e.shiftKey) {
                            e.preventDefault();
                            firstButtonRef.current.focus();
                        }
                    }}}>
                        Apply
                    </Button>
                </ButtonsGroup>
            </div>
        );
    }, [filters, filtersArr]);

    const FilterIconComponent = () => {
        return (
            <div
                // role="img"
                // aria-expanded={isDropDownVisible}
                // aria-label={`${title} filter, ${filters.length} filters selected`}
                style={{ lineHeight: "13px" }}
            >
                {filters.length > 0 ? (
                    <FilterFilled style={{ fontSize: "13px" }} />
                ) : (
                    <FilterOutlined style={{ fontSize: "13px" }} />
                )}
            </div>
        );
    };

    return {
        title,
        key,
        dataIndex,
        filters: filtersArr,
        onFilter: (value, record) =>
            record[onFilterVal]?.includes(value),
        filteredValue: filters,
        // The Ant Design `filterDropdown` prop receives the `columnIndex` via props
        filterDropdown: (props) => {
            const FilterDropdownComponent = FilterDropdownContent;
            return <FilterDropdownComponent tableId={props.tableId} {...props} columnIndex={props.columnIndex} />;
        },
        filterIcon: () => (
            <FilterIconComponent />
        ),
        filterDropdownProps: {
            open: isDropDownVisible
        },
        toggleFilter: toggleDropDown
    };
};

export default createTableAntDHeaderFilter;